// src/components/Auth/Login.js
import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
// import { Lock, LogIn, Mail, Phone, MessageSquare } from "lucide-react"; // Email login icons commented out
import { Phone, MessageSquare } from "lucide-react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuthApi } from "../../hooks/useAuthApi";
// import { loginDefaultValues, loginSchema } from "../../utils/validationSchemas"; // Email login schema commented out
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
// import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs"; // Tabs commented out — email login hidden for now
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { z } from "zod";

// Function to decode JWT token
const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    return null;
  }
};

const mobileSchema = z.object({
  phone: z.string().regex(/^\d{10}$/, "Phone number must be 10 digits"),
  otp: z.string().length(6, "OTP must be 6 digits").optional(),
});

const Login = ({ onToggle, loading, setLoading, signupData }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { login: authLogin } = useAuth();
  const {
    // login: loginUser,   // Email login — commented out for now
    sendLoginOtp,
    loginWithOtp,
    loading: apiLoading,
    error: apiError,
  } = useAuthApi();

  const [otpSent, setOtpSent] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");

  const {
    register: registerMobile,
    handleSubmit: handleSubmitMobile,
    formState: { errors: mobileErrors },
    setError: setMobileError,
    setValue: setMobileValue,
  } = useForm({
    resolver: zodResolver(mobileSchema),
  });

  // Handle data passed from Signup
  React.useEffect(() => {
    if (signupData?.phone) {
      setMobileNumber(signupData.phone);
      setOtpSent(true);
      setMobileValue("phone", signupData.phone);
      toast({
        title: "Registration Successful",
        description: "Please enter the OTP sent to your phone.",
      });
    }
  }, [signupData, setMobileValue, toast]);

  // Check for offer advice
  React.useEffect(() => {
    if (searchParams.get("advice") === "true") {
      toast({
        title: "Login Required",
        description: "Please login or sign up to claim this exclusive offer.",
        variant: "default",
        duration: 5000,
      });
    }
  }, [searchParams, toast]);

  const handleLoginSuccess = (response) => {
    if (
      response?.status === 200 ||
      (response?.data && response.data.access_token)
    ) {
      const accessToken = response.data.access_token;

      localStorage.setItem("access_token", accessToken);
      localStorage.setItem("token_type", response.data.token_type || "bearer");

      const decodedToken = decodeJWT(accessToken);

      if (decodedToken) {
        console.log("Decoded JWT Token:", decodedToken);

        const userFirstName = decodedToken.first_name || "N/A";
        const userId = decodedToken.sub || "N/A";
        const userRoles = decodedToken.roles || [];
        const userPhone = decodedToken.phone_number || "";

        localStorage.setItem("user_first_name", userFirstName);
        localStorage.setItem("user_id", userId);
        localStorage.setItem("user_roles", JSON.stringify(userRoles));
        localStorage.setItem("user_name", userFirstName);

        const userData = {
          id: userId,
          first_name: userFirstName,
          phone_number: userPhone,
          roles: userRoles,
          email: "",
        };

        authLogin(userData);
      } else {
        console.warn("Could not decode JWT token");
      }

      toast({
        title: "Login successful!",
        description: "Welcome back to GEETH365.",
        duration: 3000,
      });

      const redirect = searchParams.get("redirect") || "/";
      setTimeout(() => {
        navigate(redirect);
      }, 1500);
    } else {
      setMobileError("root", {
        type: "server",
        message: "Login completed but unexpected response.",
      });
    }
  };

  // Email login submit — commented out for now
  // const onSubmit = async (formData) => {
  //   setLoading(true);
  //   try {
  //     const response = await loginUser({
  //       email: formData.email,
  //       password: formData.password,
  //     });
  //     handleLoginSuccess(response, formData.email);
  //   } catch (error) {
  //     console.error("Login error:", error);
  //     setError("root", {
  //       type: "server",
  //       message: error.response?.data?.detail || "Login failed. Please check credentials.",
  //     });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const onSendOtp = async (formData) => {
    setLoading(true);
    try {
      await sendLoginOtp({ phone: formData.phone });
      setOtpSent(true);
      setMobileNumber(formData.phone);
      toast({ title: "OTP Sent", description: "Please check your mobile for the OTP." });
    } catch (error) {
      console.error("OTP Send error:", error);
      setMobileError("root", {
        type: "server",
        message: error.response?.data?.detail || "Failed to send OTP.",
      });
    } finally {
      setLoading(false);
    }
  };

  const onMobileSubmit = async (formData) => {
    if (!otpSent) {
      await onSendOtp(formData);
    } else {
      // Verify OTP
      setLoading(true);
      try {
        const response = await loginWithOtp({
          phone: mobileNumber,
          otp: formData.otp,
        });
        handleLoginSuccess(response);
      } catch (error) {
        console.error("Mobile Login error:", error);
        setMobileError("root", {
          type: "server",
          message: error.response?.data?.detail || "Invalid OTP or Login failed.",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const isLoading = loading || apiLoading;

  return (
    <div className="space-y-4">
      {/* Email login tab — commented out for now */}
      {/* <Tabs defaultValue="email" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="email">Email Login</TabsTrigger>
          <TabsTrigger value="mobile">Mobile Login</TabsTrigger>
        </TabsList>

        <TabsContent value="email">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="login-email" className="flex items-center space-x-2">
                <Mail className="w-4 h-4" />
                <span>Email</span>
              </Label>
              <Input
                id="login-email"
                type="email"
                placeholder="Enter your email"
                className="mt-1"
                {...register("email")}
                disabled={isLoading}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="login-password" className="flex items-center space-x-2">
                <Lock className="w-4 h-4" />
                <span>Password</span>
              </Label>
              <Input
                id="login-password"
                type="password"
                placeholder="Enter your password"
                className="mt-1"
                {...register("password")}
                disabled={isLoading}
              />
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
              )}
            </div>

            <div className="flex justify-end mt-2">
              <Link to="/forgot-password" className="text-sm font-medium text-orange-600 hover:text-orange-700 hover:underline">
                Forgot Password?
              </Link>
            </div>

            {(errors.root || apiError) && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{errors.root?.message || apiError}</p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-2.5"
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Login
                </>
              )}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="mobile"> */}

      {/* Mobile OTP Login — active */}
      <form onSubmit={handleSubmitMobile(onMobileSubmit)} className="space-y-4">
        <div>
          <Label htmlFor="mobile-phone" className="flex items-center space-x-2">
            <Phone className="w-4 h-4" />
            <span>Mobile Number</span>
          </Label>
          <div className="flex">
            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
              +91
            </span>
            <Input
              id="mobile-phone"
              type="tel"
              placeholder="Enter 10 digit number"
              className="rounded-l-none"
              {...registerMobile("phone")}
              disabled={isLoading || otpSent}
            />
          </div>
          {mobileErrors.phone && (
            <p className="text-red-500 text-sm mt-1">{mobileErrors.phone.message}</p>
          )}
        </div>

        {otpSent && (
          <div>
            <Label htmlFor="mobile-otp" className="flex items-center space-x-2">
              <MessageSquare className="w-4 h-4" />
              <span>Enter OTP</span>
            </Label>
            <Input
              id="mobile-otp"
              type="text"
              placeholder="Enter 6 digit OTP"
              className="mt-1"
              {...registerMobile("otp")}
              disabled={isLoading}
            />
            {mobileErrors.otp && (
              <p className="text-red-500 text-sm mt-1">{mobileErrors.otp.message}</p>
            )}
          </div>
        )}

        {mobileErrors.root && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{mobileErrors.root?.message}</p>
          </div>
        )}

        <Button
          type="submit"
          className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-2.5"
          disabled={isLoading}
        >
          {isLoading ? "Processing..." : (
            otpSent ? "Verify & Login" : "Send OTP"
          )}
        </Button>

        {otpSent && (
          <div className="text-center mt-2">
            <button
              type="button"
              onClick={() => { setOtpSent(false); }}
              className="text-xs text-blue-600 hover:underline"
            >
              Change Number / Resend
            </button>
          </div>
        )}

        <div className="flex justify-end mt-3">
          <Link to="/forgot-password" className="text-sm font-medium text-orange-600 hover:text-orange-700 hover:underline">
            Forgot Password?
          </Link>
        </div>
      </form>

      {/* </TabsContent>
      </Tabs> */}

      <div className="text-center">
        <p className="text-sm text-gray-600">
          Don't have an account?{" "}
          <button
            type="button"
            onClick={() => onToggle("signup")}
            className="text-orange-600 hover:text-orange-700 font-medium"
            disabled={isLoading}
          >
            Sign up
          </button>
        </p>
      </div>
    </div>
  );
};

export default Login;
